import { useState } from 'react';
import { useDispatch, useMachineState } from '../app/context';
import { Button } from '../components/Button';
import { cx } from '../components/cx';
import { RoomPlan } from '../components/RoomPlan';
import { bulbNumber, bulbSummary, ROBOT_POSITIONS } from '../domain/catalog';
import { formatDuration, targetLabel } from '../domain/format';
import type { RobotPosition } from '../domain/types';
import common from './common.module.css';
import styles from './PositionScreen.module.css';

export function PositionScreen() {
  const { ward, bed, position, cycleTotal, channels, lampChannels } = useMachineState();
  const dispatch = useDispatch();
  // The operator vouches for the room before the machine can be powered from outside it.
  const [roomClear, setRoomClear] = useState(false);
  if (!ward || !bed) return null;

  const target = targetLabel(ward.code, bed);
  const select = (p: RobotPosition) => dispatch({ type: 'SELECT_POSITION', position: p });

  return (
    <section
      className={cx(common.screen, common.fadeUp, styles.screen)}
      aria-labelledby="pos-title"
    >
      <div className={cx(common.header, styles.header)}>
        <div>
          <div className={common.kicker}>DEPLOYMENT POSITION · {target}</div>
          <h2 id="pos-title" className={common.title}>
            Position Robot In Room
          </h2>
        </div>
        <Button variant="ghost" size="xs" onClick={() => dispatch({ type: 'BACK_TO_BEDS' })}>
          ← BED SELECT
        </Button>
      </div>

      <div className={styles.body}>
        {/* Touch targets on the plan mirror the list, which is the accessible control. */}
        <div className={styles.plan} aria-hidden>
          <RoomPlan className={styles.planDrawing} />
          {ROBOT_POSITIONS.map((p) => (
            <button
              key={p.id}
              type="button"
              tabIndex={-1}
              className={cx(common.tile, styles.marker, position?.id === p.id && styles.selected)}
              style={{ left: `${p.x}%`, top: `${p.y}%` }}
              onClick={() => select(p)}
            >
              <span className={styles.markerRing}>
                <span className={styles.markerDot} />
              </span>
              <span className={styles.markerLabel}>{p.label}</span>
            </button>
          ))}
        </div>

        <div className={styles.list} role="group" aria-labelledby="pos-list-heading">
          <div id="pos-list-heading" className={cx(common.label, styles.listHeading)}>
            AVAILABLE POSITIONS
          </div>
          {ROBOT_POSITIONS.map((p) => {
            const selected = position?.id === p.id;
            return (
              <button
                key={p.id}
                type="button"
                className={cx(common.tile, styles.row)}
                aria-pressed={selected}
                onClick={() => select(p)}
              >
                <span className={styles.rowLabel}>{p.label}</span>
                <span className={styles.rowStatus}>{selected ? '[ SET ]' : ''}</span>
              </button>
            );
          })}
          <div
            className={styles.bulbs}
            role="group"
            aria-labelledby="bulbs-heading"
            aria-describedby="bulbs-hint"
          >
            <div className={styles.bulbsHeader}>
              <span id="bulbs-heading" className={common.label}>
                BULBS TO SWITCH ON
              </span>
              <span id="bulbs-hint" className={styles.bulbsHint}>
                FEWER = LOWER UV-C DOSE
              </span>
            </div>
            <div className={styles.bulbRow}>
              {lampChannels.map((channel) => {
                const on = channels.includes(channel);
                return (
                  <button
                    key={channel}
                    type="button"
                    className={cx(common.tile, styles.bulb)}
                    aria-pressed={on}
                    aria-label={`Bulb ${bulbNumber(channel)}`}
                    onClick={() => dispatch({ type: 'TOGGLE_BULB', channel })}
                  >
                    <span className={styles.bulbDot} />
                    {bulbNumber(channel)}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {position && (
        <div className={common.actionBar}>
          <div>
            <div className={common.label}>DEPLOYMENT LOCKED</div>
            <div className={common.actionTitle}>
              {target} · {position.label}
            </div>
            <div className={styles.actionMeta}>
              BULBS {bulbSummary(channels, lampChannels)} · CYCLE {formatDuration(cycleTotal)}
            </div>
          </div>
          <div className={styles.actionRight}>
            <label className={styles.confirm}>
              <input
                type="checkbox"
                checked={roomClear}
                onChange={(e) => setRoomClear(e.target.checked)}
              />
              Room is clear of patients and staff
            </label>
            <Button
              variant="primary"
              size="xl"
              disabled={!roomClear}
              onClick={() => dispatch({ type: 'START_CYCLE' })}
            >
              START CYCLE
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
